import { Check, SlidersHorizontal } from "lucide-react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { catColor, CategoryIcon } from "@/lib/categories";

export default function CategoryFilter({
  options,
  hidden,
  onToggle,
  onShowAll,
  onHideAll,
  viewMode,
  onViewModeChange,
  viewportAware,
}) {
  const activeCount = options.filter((o) => !hidden.has(o.name)).length;
  const list = viewportAware
    ? [...options].sort((a, b) => {
        const ap = (a.visibleCount || 0) > 0 ? 0 : 1;
        const bp = (b.visibleCount || 0) > 0 ? 0 : 1;
        if (ap !== bp) return ap - bp;
        return a.name.localeCompare(b.name, "de");
      })
    : options;

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          data-testid="category-filter-button"
          className="glass flex items-center gap-2 rounded-2xl px-4 py-2.5 text-sm font-semibold text-slate-700 transition-transform hover:scale-[1.03] active:scale-[0.97]"
        >
          <SlidersHorizontal size={16} className="text-primary" />
          Filter
          <span className="ml-0.5 rounded-full bg-primary/10 px-1.5 py-0.5 text-[11px] font-bold text-primary">
            {activeCount}/{options.length}
          </span>
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        className="w-72 overflow-hidden rounded-2xl p-0"
        data-testid="category-filter-panel"
      >
        {/* View switch */}
        {onViewModeChange && (
          <div className="border-b border-slate-100 p-3">
            <p className="mb-1.5 text-[10px] font-bold uppercase tracking-[0.15em] text-slate-400">
              Ansicht
            </p>
            <div className="grid grid-cols-2 gap-1 rounded-xl bg-slate-100 p-1">
              <button
                data-testid="view-mode-category"
                onClick={() => onViewModeChange("category")}
                className={`rounded-lg px-2 py-1.5 text-xs font-semibold transition-colors ${
                  viewMode === "category"
                    ? "bg-white text-slate-900 shadow-sm"
                    : "text-slate-500"
                }`}
              >
                Nach Kategorie
              </button>
              <button
                data-testid="view-mode-recent"
                onClick={() => onViewModeChange("recent")}
                className={`rounded-lg px-2 py-1.5 text-xs font-semibold transition-colors ${
                  viewMode === "recent"
                    ? "bg-white text-slate-900 shadow-sm"
                    : "text-slate-500"
                }`}
              >
                Neueste zuerst
              </button>
            </div>
          </div>
        )}

        <div className="flex items-center justify-between border-b border-slate-100 px-4 py-3">
          <p className="font-display text-sm font-bold text-slate-900">
            Kategorien
          </p>
          <div className="flex gap-3 text-xs font-semibold">
            <button
              onClick={onShowAll}
              data-testid="filter-show-all"
              className="text-primary hover:underline"
            >
              Alle
            </button>
            <button
              onClick={onHideAll}
              data-testid="filter-hide-all"
              className="text-slate-400 hover:underline"
            >
              Keine
            </button>
          </div>
        </div>
        <div
          className="max-h-[52vh] overflow-y-auto p-1.5"
          data-testid="category-filter-list"
        >
          {options.length === 0 && (
            <p className="px-3 py-6 text-center text-sm text-slate-400">
              Keine Kategorien
            </p>
          )}
          {list.map((o) => {
            const checked = !hidden.has(o.name);
            const present = viewportAware && (o.visibleCount || 0) > 0;
            const absent = viewportAware && (o.visibleCount || 0) === 0;
            return (
              <button
                key={o.name}
                data-testid={`filter-option-${o.name}`}
                onClick={() => onToggle(o.name)}
                className={`flex w-full items-center gap-3 rounded-xl px-2.5 py-2 text-left transition-all hover:bg-slate-50 ${
                  absent ? "opacity-45" : ""
                }`}
              >
                <span
                  className={`grid h-5 w-5 shrink-0 place-items-center rounded-md border-2 transition-colors ${
                    checked ? "border-transparent" : "border-slate-300 bg-white"
                  }`}
                  style={{ background: checked ? catColor(o.name) : undefined }}
                >
                  {checked && (
                    <Check size={13} className="text-white" strokeWidth={3} />
                  )}
                </span>
                <span
                  className="grid h-7 w-7 shrink-0 place-items-center rounded-lg"
                  style={{ background: catColor(o.name) }}
                >
                  <CategoryIcon category={o.name} color="#fff" size={15} />
                </span>
                <span className="min-w-0 flex-1 truncate text-sm font-medium text-slate-800">
                  {o.name}
                </span>
                <span
                  className={`text-xs tabular-nums ${
                    present
                      ? "font-black text-slate-700"
                      : "font-semibold text-slate-300"
                  }`}
                >
                  {viewportAware ? o.visibleCount || 0 : o.count}
                </span>
              </button>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}
